import { ReactNode, useEffect, useRef, useState } from 'react'
import { useAuth as useClerkAuth } from '@clerk/react'

import { useAuthStore } from '@/features/auth/authStore'
import {
  CLERK_JWT_TEMPLATE,
  CLERK_TOKEN_FETCH_CONTEXT_KEY,
} from '@/features/auth/constants'
import { useCurrentUser } from '@/hooks/useAuth'

import { Spinner } from '../../components/ui'
import { ROUTES } from '../../utils/routes'

type AuthProviderProps = {
  children: ReactNode
  autoConnect?: boolean
}

export const AuthProvider = ({
  children,
  autoConnect = true,
}: AuthProviderProps) => {
  const [initError, setInitError] = useState<string | null>(null)
  const [isBootstrapping, setIsBootstrapping] = useState<boolean>(true)

  const setToken = useAuthStore((state) => state.setToken)
  const resetStore = useAuthStore((state) => state.reset)
  const token = useAuthStore((state) => state.token)

  // Clerk hooks for token synchronization
  const { isLoaded, isSignedIn, getToken, signOut } = useClerkAuth()

  // Track if we've redirected to 500 page to avoid loops
  const hasRedirectedTo500 = useRef(false)

  // Centralized hook to fetch backend user
  // We ONLY enable this once we definitively have a token in the Zustand store AND Clerk has initialized
  const {
    data: currentUser,
    isLoading: isUserLoading,
    isFetching: isUserFetching,
    isError: isUserFetchError,
    error: userFetchError,
  } = useCurrentUser({ enabled: autoConnect && Boolean(token) && isLoaded })

  const [isClerkSynced, setIsClerkSynced] = useState<boolean>(false)

  // 1. Core State Machine: Sync Clerk Token to App State
  useEffect(() => {
    if (!isLoaded) return // Wait for Clerk to initialize fully

    if (isSignedIn) {
      setIsBootstrapping(true)
      getToken({ template: CLERK_JWT_TEMPLATE })
        .then((newToken) => {
          if (newToken) {
            localStorage.setItem('token', newToken)
            setToken(newToken)
            setIsClerkSynced(true)
            // Note: We DO NOT set isBootstrapping(false) here. We must wait for `useCurrentUser` to finish.
          } else {
            // Logged into Clerk, but no token returned. Treat as logged out.
            localStorage.removeItem('token')
            setToken(null)
            resetStore()
            setIsClerkSynced(true)
            setIsBootstrapping(false)
          }
        })
        .catch((error) => {
          localStorage.removeItem('token')
          setToken(null)
          resetStore()

          // Prevent redirect loop if we're already on the 500 page
          if (
            !hasRedirectedTo500.current &&
            globalThis.location.pathname !== ROUTES.INTERNAL_SERVER_ERROR
          ) {
            hasRedirectedTo500.current = true

            // Save context for retry on 500 page
            sessionStorage.setItem(
              CLERK_TOKEN_FETCH_CONTEXT_KEY,
              JSON.stringify({
                type: 'clerk-token-fetch',
                errorStatus: error?.status || null,
                sourceUrl: globalThis.location.pathname,
                timestamp: Date.now(),
              })
            )

            // Redirect to 500 error page
            globalThis.location.href = ROUTES.INTERNAL_SERVER_ERROR
            return
          }

          setInitError(
            'Failed to synchronize authentication token. Please try again.'
          )
          setIsClerkSynced(true)
          setIsBootstrapping(false)
        })
    } else {
      // User is definitively logged out of Clerk
      localStorage.removeItem('token')
      setToken(null)
      resetStore()
      setIsClerkSynced(true)
      setIsBootstrapping(false) // Safe to render Router (it will kick them to /login)
    }
  }, [isLoaded, isSignedIn, getToken, setToken, resetStore])

  // 2. Core State Machine: Sync Backend User to App State
  useEffect(() => {
    // IMPORTANT: Wait until Clerk synchronization is complete before evaluating
    if (!isClerkSynced) return

    // Only process if we have a token (otherwise step 1 already finished the bootstrap)
    if (!token) {
      if (isBootstrapping) setIsBootstrapping(false)
      return
    }

    if (isUserFetchError) {
      const message =
        userFetchError instanceof Error
          ? userFetchError.message
          : 'Unable to verify your session. Please sign in again.'
      setInitError(message)
      resetStore()
      localStorage.removeItem('token')
      setIsBootstrapping(false)
      return
    }

    if (!isUserLoading && !isUserFetching) {
      if (currentUser) {
        useAuthStore.setState({
          user: currentUser,
          isAuthenticated: true,
          isLoading: false,
          error: null,
        })
        setInitError(null)
      } else {
        // Query finished but no user returned, treat as error/logged out
        resetStore()
        localStorage.removeItem('token')
      }

      // Regardless of user found or not, the loading pipeline is finished
      setIsBootstrapping(false)
    }
  }, [
    token,
    currentUser,
    isUserLoading,
    isUserFetching,
    isUserFetchError,
    userFetchError,
    resetStore,
    isClerkSynced,
    isBootstrapping,
  ])

  // Handle global auth:logout events
  useEffect(() => {
    const handleAuthLogout = async () => {
      // 1. Clear local state
      localStorage.removeItem('token')
      setToken(null)
      resetStore()

      // 2. Sign out of Clerk properly BEFORE navigating
      try {
        if (signOut) {
          await signOut()
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Error signing out of Clerk:', err)
        // Ignore error
      }

      // 3. Navigate back to login
      globalThis.location.href = ROUTES.LOGIN
    }

    globalThis.addEventListener('auth:logout', handleAuthLogout)
    return () => {
      globalThis.removeEventListener('auth:logout', handleAuthLogout)
    }
  }, [signOut, setToken, resetStore])

  // 3. Render Gatekeeper
  // Completely block rendering of the application (protecting the Router) until the entire auth pipeline resolves.

  if (!isLoaded || isBootstrapping) {
    return (
      <div className="flex items-center content-center justify-center min-h-screen">
        <Spinner className="size-8 text-quaternary-600" />
      </div>
    )
  }

  return (
    <>
      {initError ? (
        <div
          role="alert"
          className="px-4 py-2 m-4 mb-4 text-sm text-red-700 border border-red-100 rounded-md bg-red-50"
        >
          {initError}
        </div>
      ) : null}
      {children}
    </>
  )
}

export default AuthProvider
